import { Input } from "components/ui/Input/Input";
import type { SegmentedControlItem } from "components/ui/SegmentedControl/SegmentedControl";
import { SegmentedControl } from "components/ui/SegmentedControl/SegmentedControl";
import { Select, type SelectOption } from "components/ui/Select/Select";
import { Tabs } from "components/ui/Tabs/Tabs";
import {
  type DeliverySeries,
  useDeliverySeriesList,
} from "hooks/use-delivery-series-api";
import type {
  ReplyParentType,
  ReplyResponseStatus,
  ReplyStats,
} from "hooks/use-email-replies-api";
import { Megaphone, Search, Send } from "lucide-react";
import { type FC, useEffect, useMemo, useState } from "react";

export type ReplyLabel = "all" | ReplyParentType;
export type ReplyPeriod = "all" | "24h" | "7d" | "30d";

export type ReplyFilterBarProps = {
  status: ReplyResponseStatus | "all";
  label: ReplyLabel;
  period: ReplyPeriod;
  query: string;
  /** 配信シリーズで絞り込み. null = 絞り込まない. */
  seriesId: string | null;
  stats: ReplyStats | null;
  onStatusChange: (next: ReplyResponseStatus | "all") => void;
  onLabelChange: (next: ReplyLabel) => void;
  onPeriodChange: (next: ReplyPeriod) => void;
  onQueryChange: (next: string) => void;
  onSeriesChange: (next: string | null) => void;
};

type StatusOption = {
  value: ReplyResponseStatus | "all";
  label: string;
  countKey: keyof ReplyStats | null;
};

const STATUS_OPTIONS: ReadonlyArray<StatusOption> = [
  { value: "all", label: "全て", countKey: "total" },
  { value: "unresponded", label: "未対応", countKey: "unresponded" },
  { value: "in_progress", label: "対応中", countKey: "inProgress" },
  { value: "completed", label: "完了", countKey: "completed" },
];

const LABEL_OPTIONS: ReadonlyArray<SegmentedControlItem<ReplyLabel>> = [
  { value: "all", label: "全て" },
  { value: "delivery", label: "一斉", icon: Megaphone },
  { value: "sent_email", label: "個別", icon: Send },
];

const PERIOD_OPTIONS: ReadonlyArray<{ value: ReplyPeriod; label: string }> = [
  { value: "all", label: "全期間" },
  { value: "24h", label: "24 時間" },
  { value: "7d", label: "7 日" },
  { value: "30d", label: "30 日" },
];

/**
 * ステータス絞込タブ + ラベル / 期間 / 検索 を統合したフィルターバー.
 * Tabs / SegmentedControl / Input は components/ui のリーフを使用する.
 * @param {ReplyFilterBarProps} props フィルター値とハンドラ
 * @return {JSX.Element} フィルターバー
 */
export const ReplyFilterBar: FC<ReplyFilterBarProps> = ({
  status,
  label,
  period,
  query,
  seriesId,
  stats,
  onStatusChange,
  onLabelChange,
  onPeriodChange,
  onQueryChange,
  onSeriesChange,
}) => {
  const tabsItems = useMemo(
    () =>
      STATUS_OPTIONS.map(opt => ({
        value: opt.value,
        label: opt.label,
        count: opt.countKey && stats ? stats[opt.countKey] : null,
      })),
    [stats],
  );

  return (
    <div className="bg-white rounded-lg border border-slate-200 px-3 pt-1 pb-2 flex flex-col gap-2">
      <Tabs<ReplyResponseStatus | "all">
        value={status}
        items={tabsItems}
        onChange={onStatusChange}
      />
      <div className="flex items-center gap-3 flex-wrap">
        <SegmentedControl<ReplyLabel>
          value={label}
          label="ラベル"
          items={LABEL_OPTIONS}
          onChange={onLabelChange}
        />
        <div className="h-5 w-px bg-slate-200" aria-hidden />
        <SeriesSelect value={seriesId} onChange={onSeriesChange} />
        <div className="h-5 w-px bg-slate-200" aria-hidden />
        <PeriodSelect value={period} onChange={onPeriodChange} />
        <div className="ml-auto">
          <DebouncedSearchInput value={query} onChange={onQueryChange} />
        </div>
      </div>
    </div>
  );
};

type SeriesSelectProps = {
  value: string | null;
  onChange: (next: string | null) => void;
};

const ALL_SERIES_VALUE = "__all__";

/**
 * 配信シリーズで絞り込むセレクト. active シリーズだけ列挙.
 * components/ui/Select (react-select) を利用し他のフォーム要素と統一する.
 * @param {SeriesSelectProps} props 値とハンドラ
 * @return {JSX.Element} セレクト
 */
const SeriesSelect: FC<SeriesSelectProps> = ({ value, onChange }) => {
  const { items, loading } = useDeliverySeriesList({
    status: "active",
    limit: 100,
  });
  const seriesById = useMemo(() => {
    const map = new Map<string, DeliverySeries>();
    for (const s of items) map.set(s.id, s);
    return map;
  }, [items]);

  // 現在の value が一覧に無い場合 (archived 等) でも表示するための fallback option
  const orphan = value && !seriesById.has(value) ? value : null;

  const options = useMemo<SelectOption[]>(() => {
    const base: SelectOption[] = [
      { value: ALL_SERIES_VALUE, label: "全て" },
      ...items.map(s => ({ value: s.id, label: s.name })),
    ];
    if (orphan) base.push({ value: orphan, label: "(現在のシリーズ)" });
    return base;
  }, [items, orphan]);

  return (
    <label className="flex items-center gap-1 text-xs text-base-content/60">
      シリーズ
      <div className="w-44">
        <Select
          options={options}
          value={value ?? ALL_SERIES_VALUE}
          onChange={next =>
            onChange(!next || next === ALL_SERIES_VALUE ? null : next)
          }
          isDisabled={loading && items.length === 0}
          placeholder="全て"
          size="sm"
        />
      </div>
    </label>
  );
};

type PeriodSelectProps = {
  value: ReplyPeriod;
  onChange: (next: ReplyPeriod) => void;
};

/**
 * 期間セレクト (受信時刻ベース). components/ui/Select で他要素と統一.
 * @param {PeriodSelectProps} props 値とハンドラ
 * @return {JSX.Element} セレクト
 */
const PeriodSelect: FC<PeriodSelectProps> = ({ value, onChange }) => {
  const options = useMemo<SelectOption[]>(
    () => PERIOD_OPTIONS.map(opt => ({ value: opt.value, label: opt.label })),
    [],
  );
  return (
    <label className="flex items-center gap-1 text-xs text-base-content/60">
      期間
      <div className="w-28">
        <Select
          options={options}
          value={value}
          onChange={next => onChange((next ?? "all") as ReplyPeriod)}
          size="sm"
        />
      </div>
    </label>
  );
};

type DebouncedSearchInputProps = {
  value: string;
  onChange: (next: string) => void;
};

/**
 * 検索ボックス (250ms debounce). components/ui/Input の上に Search アイコンを重ねる.
 * @param {DebouncedSearchInputProps} props 値とハンドラ
 * @return {JSX.Element} 検索ボックス
 */
const DebouncedSearchInput: FC<DebouncedSearchInputProps> = ({
  value,
  onChange,
}) => {
  const [draft, setDraft] = useState(value);

  // 親側からの value 変更 (URL 由来等) を反映
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // debounce: draft → 親へコミット
  useEffect(() => {
    if (draft === value) return;
    const handle = window.setTimeout(() => {
      onChange(draft);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [draft, value, onChange]);

  return (
    <div className="relative w-64">
      <Search
        size={14}
        className="absolute left-2 top-1/2 -translate-y-1/2 text-base-content/40 pointer-events-none"
        aria-hidden
      />
      <Input
        data-reply-search-input
        size="sm"
        type="search"
        placeholder="from / 件名 / 本文 を検索"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        style={{ paddingLeft: "1.75rem" }}
      />
    </div>
  );
};
